// Registers PdfJobContext recovery builders for the quiz PDF flow and
// bootstraps the job box from any server-side jobs already in progress.
//
// Mounted once inside <PdfJobProvider> in App.js. Has access to FetchContext
// (for the privateFetch instance) and to usePdfJob's bootstrapServerJobs.
//
// Recovery hint shapes (defined by services/quizPdfFlow.js):
//   { kind: 'quiz',         quizId, quizName, filename }
//   { kind: 'chapter-quiz', chapterId, category, chapterName, activeTab, filename }

import { useContext, useEffect, useRef } from 'react';
import { FetchContext } from 'context/FetchContext';
import { usePdfJob, registerPdfJobRecoveryBuilder } from 'context/PdfJobContext';
import {
  buildQuizPdfJobSpec,
  buildChapterQuizPdfJobSpec,
  getQuizPdfFilename,
} from 'services/quizPdfFlow';

// Endpoint added in the admin backend (vc-admin-mcs) — returns the set of
// quiz+chapter PDF jobs whose status is currently queued/processing for the
// authenticated tenant. Safe to 404/fail silently if the backend hasn't been
// updated yet (recovery from localStorage still works).
const ACTIVE_JOBS_ENDPOINT = '/v1/admin/quiz/pdf/active-jobs';

const QuizPdfRecovery = () => {
  const { privateFetch } = useContext(FetchContext) || {};
  const { bootstrapServerJobs } = usePdfJob() || {};
  const bootstrappedRef = useRef(false);

  // ── 1. Register recovery builders ─────────────────────────────────────────
  useEffect(() => {
    if (!privateFetch) return undefined;

    // Jobs that were still QUEUED locally when the user reloaded never POSTed
    // to the server — those need a fresh start. Jobs that had already begun
    // (SENDING/PROCESSING/RECOVERING) are already on the server and should
    // just re-attach.
    const pickStartMode = (previousStatus) => (previousStatus === 'QUEUED' ? 'start' : 'attach');

    const unregisterQuiz = registerPdfJobRecoveryBuilder('quiz', (hint, ctx) => {
      if (!hint?.quizId) return null;
      const spec = buildQuizPdfJobSpec({
        privateFetch,
        quizId: hint.quizId,
        quizName: hint.quizName,
        filename: hint.filename,
        startMode: pickStartMode(ctx?.previousStatus),
      });
      return { run: spec.run, downloadFn: spec.downloadFn };
    });

    const unregisterChapter = registerPdfJobRecoveryBuilder('chapter-quiz', (hint, ctx) => {
      if (!hint?.chapterId || !hint?.category) return null;
      const spec = buildChapterQuizPdfJobSpec({
        privateFetch,
        chapterId: hint.chapterId,
        category: hint.category,
        chapterName: hint.chapterName,
        activeTab: hint.activeTab,
        filename: hint.filename,
        startMode: pickStartMode(ctx?.previousStatus),
      });
      return { run: spec.run, downloadFn: spec.downloadFn };
    });

    return () => {
      unregisterQuiz();
      unregisterChapter();
    };
  }, [privateFetch]);

  // ── 2. Bootstrap from server-side active jobs (cross-device discovery) ────
  useEffect(() => {
    if (!privateFetch || !bootstrapServerJobs || bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const response = await privateFetch.get(ACTIVE_JOBS_ENDPOINT);
        if (cancelled) return;
        const list = response?.data?.data || response?.data || [];
        if (!Array.isArray(list) || list.length === 0) return;

        const seeds = [];
        for (const item of list) {
          if (!item) continue;
          if (item.kind === 'quiz' && item.quizId) {
            const quizName = item.quizName || item.name || 'Quiz';
            const filename = item.filename || getQuizPdfFilename(quizName);
            seeds.push({
              id: `srv-quiz-${item.quizId}`,
              title: `Quiz: ${quizName}`,
              progress: Number(item.progress || 0),
              filename,
              recoveryHint: { kind: 'quiz', quizId: item.quizId, quizName, filename },
            });
          } else if (item.kind === 'chapter-quiz' && item.chapterId && item.category) {
            const chapterName = item.chapterName || item.name || 'Chapter';
            const activeTab = item.activeTab || 'qb';
            const filename = item.filename || getQuizPdfFilename(`${chapterName}_${item.category}`);
            const titleSuffix =
              activeTab === 'qb' ? 'Q.Bank' : activeTab === 'ncert' ? 'NCERT' : 'Past Papers';
            seeds.push({
              id: `srv-chapter-${item.chapterId}-${item.category}`,
              title: `${chapterName} — All ${titleSuffix} PDFs`,
              progress: Number(item.progress || 0),
              filename,
              recoveryHint: {
                kind: 'chapter-quiz',
                chapterId: item.chapterId,
                category: item.category,
                chapterName,
                activeTab,
                filename,
              },
            });
          }
        }
        if (seeds.length > 0) bootstrapServerJobs(seeds);
      } catch (err) {
        // 404 = endpoint not deployed yet, or no active jobs. Silent.
        if (err?.response?.status && err.response.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn('Quiz PDF active-jobs bootstrap failed', err?.message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [privateFetch, bootstrapServerJobs]);

  return null;
};

export default QuizPdfRecovery;
